// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { solutionCardStyles } from "@rugby/App/Widgets/SolutionCard/SolutionCard.Styles";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    imgSrc = "imgSrc",
    title = "title",
    content = "content",
    destination = "destination",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imgSrc]: string;
        [fields.title]: string;
        [fields.content]: string;
        [fields.destination]: LinkFieldValue;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const link = getLink(state, ownProps.fields.destination);
    return {
        url: link?.url,
    };
};

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const SolutionCard: FC<Props> = ({ url, fields }) => {
    const cardImage = <LazyImage src={fields.imgSrc} altText={fields.title} {...solutionCardStyles.image} />;

    return (
        <StyledWrapper {...solutionCardStyles.wrapper} data-test-selector="solutionCard">
            {url ? <Link href={url}>{cardImage}</Link> : cardImage}
            <Typography {...solutionCardStyles.title} data-test-selector="solutionCard_title">
                {fields.title}
            </Typography>
            <Typography {...solutionCardStyles.content} data-test-selector="solutionCard_content">
                {fields.content}
            </Typography>
            {url && (
                <Link href={url} {...solutionCardStyles.link} data-test-selector="learn_more">
                    {translate("Learn More")}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SolutionCard),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.imgSrc,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                isRequired: true,
                defaultValue: "",
            },
            {
                name: fields.title,
                fieldType: "General",
                editorTemplate: "TextField",
                isRequired: true,
                defaultValue: "",
            },
            {
                name: fields.content,
                fieldType: "General",
                editorTemplate: "MultilineTextField",
                isRequired: true,
                defaultValue: "",
            },
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                isRequired: true,
                fieldType: "General",
                allowCategories: items => false,
            },
        ],
    },
};

export default widgetModule;
